import React from 'react';
import styled from 'styled-components';
import SectionCard from './SectionCard';
import DataTable from './DataTable';
import { TextBlock } from 'bench-internal-theme';
import { colors } from 'bench-style-constants';
import { FinancialAccount, TransferApp, LoanAndLease } from '../types';

const TextFieldContainer = styled.div`
  display: grid;
  gap: 8px;
`;

const TextFieldLabel = styled(TextBlock).attrs({
  styleType: 'small',
  color: colors.smoke,
  uppercase: true,
})``;

const TextFieldValue = styled(TextBlock).attrs({
  styleType: 'medium',
  color: colors.charcoal,
})`
  padding: 12px;
  background-color: ${colors.pearl};
  border-radius: 4px;
  min-height: 40px;
`;

const CompletionBanner = styled.div`
  background-color: ${colors.shoot};
  border: 1px solid ${colors.stem};
  border-radius: 4px;
  padding: 12px 16px;
  margin-bottom: 16px;
`;

const CompletionText = styled(TextBlock).attrs({
  styleType: 'medium',
  color: colors.stem,
})`
  margin: 0;
`;

interface AccountsToTrackSectionProps {
  financialAccounts?: FinancialAccount[];
  transferApps?: TransferApp[];
  loansAndLeases?: LoanAndLease[];
  revenueCollection?: string;
  payroll?: string;
  other?: string;
  accountsToTrackComplete?: boolean;
}

const financialAccountColumns = [
  { key: 'institution', label: 'Institution' },
  { key: 'accountHolder', label: 'Account Holder' },
  { key: 'accountType', label: 'Account Type' },
  { key: 'last4Digits', label: 'Last 4 Digits' },
  { key: 'businessUsePercentage', label: 'Business Use %' },
];

const transferAppColumns = [
  { key: 'appName', label: 'App Name' },
  { key: 'accountHolder', label: 'Account Holder' },
  { key: 'businessUsePercentage', label: 'Business Use %' },
];

const loanColumns = [
  { key: 'institution', label: 'Institution' },
  { key: 'accountHolder', label: 'Account Holder' },
  { key: 'loanPurpose', label: 'Loan Purpose' },
];

const formatFinancialAccountRows = (accounts?: FinancialAccount[]) => {
  if (!accounts || accounts.length === 0) return [];
  return accounts.map(account => ({
    institution: account.institution,
    accountHolder: account.accountHolder,
    accountType: account.accountType,
    last4Digits: account.last4Digits,
    businessUsePercentage:
      account.businessUsePercentage != null ? `${account.businessUsePercentage}%` : '-',
  }));
};

const formatTransferAppRows = (apps?: TransferApp[]) => {
  if (!apps || apps.length === 0) return [];
  return apps.map(app => ({
    appName: app.appName,
    accountHolder: app.accountHolder,
    businessUsePercentage:
      app.businessUsePercentage != null ? `${app.businessUsePercentage}%` : '-',
  }));
};

const formatLoanRows = (loans?: LoanAndLease[]) => {
  if (!loans || loans.length === 0) return [];
  return loans.map(loan => ({
    institution: loan.institution,
    accountHolder: loan.accountHolder,
    loanPurpose: loan.loanPurpose,
  }));
};

const AccountsToTrackSection: React.FC<AccountsToTrackSectionProps> = ({
  financialAccounts,
  transferApps,
  loansAndLeases,
  revenueCollection,
  payroll,
  other,
  accountsToTrackComplete,
}) => (
  <SectionCard title="Accounts to Track">
    {accountsToTrackComplete && (
      <CompletionBanner>
        <CompletionText>Client has finished entering accounts</CompletionText>
      </CompletionBanner>
    )}

    <DataTable
      title="Financial Accounts"
      columns={financialAccountColumns}
      rows={formatFinancialAccountRows(financialAccounts)}
      emptyMessage="No financial accounts listed"
    />

    <DataTable
      title="Transfer Apps"
      columns={transferAppColumns}
      rows={formatTransferAppRows(transferApps)}
      emptyMessage="No transfer apps listed"
    />

    <DataTable
      title="Loans and Leases"
      columns={loanColumns}
      rows={formatLoanRows(loansAndLeases)}
      emptyMessage="No loans or leases listed"
    />

    <TextFieldContainer>
      <TextFieldLabel>Revenue Collection</TextFieldLabel>
      <TextFieldValue>{revenueCollection || 'Not specified'}</TextFieldValue>
    </TextFieldContainer>

    <TextFieldContainer>
      <TextFieldLabel>Payroll</TextFieldLabel>
      <TextFieldValue>{payroll || 'Not specified'}</TextFieldValue>
    </TextFieldContainer>

    <TextFieldContainer>
      <TextFieldLabel>Other</TextFieldLabel>
      <TextFieldValue>{other || 'Not specified'}</TextFieldValue>
    </TextFieldContainer>
  </SectionCard>
);

export default AccountsToTrackSection;
